import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { CountryFlat } from '../../../models/country';
import { Helpers } from '../../../utilities/helpers';
import { LutService } from '../../../services/lut.service';
import { FamilyStatusCheckService } from '../../../services/family-status-check.service';
import { FamilyStatusProblemType } from '../../../models/enums/familyStatusProblemType.enum';
import { Capitalization } from '../../../models/enums/capitalization.enum';
import { FamilyStatusResultDto } from '../../../models/api/familyStatusResultDto';
import { FamilyStatusProblemTypeDto } from '../../../models/api/familyStatusProblemTypeDto';

@Component({
  selector: 'app-check-family-status',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    RouterLink
  ],
  templateUrl: './check-family-status.component.html',
  styleUrl: './check-family-status.component.scss'
})
export class CheckFamilyStatusComponent implements OnInit {
  private fb = inject(FormBuilder);
  private lutService = inject(LutService);
  private familyStatusCheckService = inject(FamilyStatusCheckService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);

  ProblemType = FamilyStatusProblemType;
  Capitalization = Capitalization;

  problemTypes: FamilyStatusProblemTypeDto[] = [];
  countries: CountryFlat[] = [];
  measurements = Helpers.measurements();
  capitalizations = [
    { value: Capitalization.Small, name: 'Small' },
    { value: Capitalization.Medium, name: 'Medium' },
    { value: Capitalization.Large, name: 'Large' }
  ];

  results: FamilyStatusResultDto[] = [];
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  sortBy = 'Name';
  ascending = true;
  hasSearched = false;
  lastSearchedProblemType: FamilyStatusProblemType | null = null;

  /** Review keys whose save is in flight, so their checkboxes disable until it lands. */
  reviewingRows = new Set<string>();

  /**
   * The Reviewed column exists only for the checks that support it, and only once a search has
   * run — it belongs to the results, not to whatever is currently picked in the filter bar.
   */
  get displayedColumns(): string[] {
    const columns = ['name', 'company', 'country', 'year', 'occupation', 'status', 'capitalization'];
    return this.supportsReview(this.lastSearchedProblemType) ? [...columns, 'reviewed'] : columns;
  }

  filtersForm = this.fb.group({
    problemType: [null as FamilyStatusProblemType | null, Validators.required],
    countryId: [''],
    year: [''],
    capitalization: [''],
    hideReviewed: [false]
  });

  ngOnInit(): void {
    this.lutService.getVisibleCountries().subscribe({
      next: (countries) => this.countries = countries.sort((a, b) => a.name.localeCompare(b.name)),
      error: () => this.toast.error('Error loading countries')
    });

    this.familyStatusCheckService.getProblemTypes().subscribe({
      next: (types) => this.problemTypes = types,
      error: () => this.toast.error('Error loading problem types')
    });

    this.filtersForm.controls.problemType.valueChanges.subscribe((problemType) => {
      if (!this.supportsCapitalization(problemType)) {
        this.filtersForm.controls.capitalization.setValue('');
      }
    });
  }

  supportsReview(problemType: FamilyStatusProblemType | null): boolean {
    if (problemType === null || problemType === undefined) return false;
    return this.problemTypes.find(t => t.value === problemType)?.supportsReview ?? false;
  }

  /** A review belongs to the director at the company, whatever year the row is from. */
  reviewKey(row: FamilyStatusResultDto): string {
    return `${row.directorId}|${row.companyId}`;
  }

  /**
   * Confirms a director is correct at a company so later runs leave them out, or takes that
   * back. The mark covers every year, so every row of that director at that company on the page
   * follows the one ticked. Rows are kept in place rather than reloaded — the reviewer works
   * down the list, and pulling the page out from under them on every tick would be worse than
   * the repetition this fixes. When the list hides reviewed rows the page is reloaded instead:
   * the server drops every year of the director, including rows on later pages, so removing
   * only the ones in view would leave the total and the paging wrong.
   */
  onReviewedChange(row: FamilyStatusResultDto, reviewed: boolean): void {
    const key = this.reviewKey(row);
    if (!row.directorId || this.reviewingRows.has(key)) return;

    this.reviewingRows.add(key);
    this.familyStatusCheckService.setReviewed(row.problemType, row.directorId, row.companyId, reviewed).subscribe({
      next: () => {
        this.reviewingRows.delete(key);
        this.results.filter(r => this.reviewKey(r) === key).forEach(r => r.reviewed = reviewed);

        if (reviewed && this.filtersForm.controls.hideReviewed.value) {
          this.loadResults();
        }
      },
      error: () => {
        this.reviewingRows.delete(key);
        this.toast.error('Error saving the review');
      }
    });
  }

  supportsCapitalization(problemType: FamilyStatusProblemType | null): boolean {
    return problemType === FamilyStatusProblemType.CeoChairmanMissingFamilyStatus
      || problemType === FamilyStatusProblemType.FamilyOccupationMismatch
      || problemType === FamilyStatusProblemType.OccupationFamilyStatusMismatch;
  }

  getProblemTypeLabel(value: FamilyStatusProblemType): string {
    return this.problemTypes.find(t => t.value === value)?.label ?? FamilyStatusProblemType[value];
  }

  getCapitalizationLabel(value: Capitalization | null | undefined): string {
    if (value === null || value === undefined) return '';
    return Capitalization[value] ?? '';
  }

  getCompanyQueryParams(year?: number): Record<string, string | number> {
    const params: Record<string, string | number> = { tab: 'family' };
    if (year !== undefined && year !== null) params['year'] = year;
    return params;
  }

  onSearch(): void {
    if (this.filtersForm.invalid) {
      this.filtersForm.markAllAsTouched();
      return;
    }

    this.currentPage = 1;
    this.loadResults();
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.loadResults();
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadResults();
  }

  onExport(): void {
    if (this.filtersForm.invalid) {
      this.filtersForm.markAllAsTouched();
      return;
    }

    const form = this.filtersForm.value;
    if (form.problemType === null || form.problemType === undefined) {
      return;
    }

    this.spinnerService.show();
    this.familyStatusCheckService.exportCsv({
      problemType: form.problemType,
      countryId: form.countryId || undefined,
      year: form.year ? +form.year : undefined,
      capitalization: form.capitalization !== '' && form.capitalization !== null && form.capitalization !== undefined
        ? +form.capitalization
        : undefined,
      hideReviewed: form.hideReviewed ?? false
    }).subscribe({
      next: (response) => {
        this.downloadCsv(response.body!, this.getFilename(response, `family_status_check_${FamilyStatusProblemType[form.problemType!]}`));
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error exporting CSV');
      }
    });
  }

  onExportAll(): void {
    const form = this.filtersForm.value;

    this.spinnerService.show();
    this.familyStatusCheckService.exportAllCsv(
      form.countryId || undefined,
      form.year ? +form.year : undefined,
      form.capitalization !== '' && form.capitalization !== null && form.capitalization !== undefined
        ? +form.capitalization
        : undefined
    ).subscribe({
      next: (response) => {
        this.downloadCsv(response.body!, this.getFilename(response, 'family_status_check_all'));
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error exporting all CSV');
      }
    });
  }

  private getFilename(response: { headers: { get(name: string): string | null } }, fallbackPrefix: string): string {
    const disposition = response.headers.get('Content-Disposition');
    if (disposition) {
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
      if (match?.[1]) {
        return decodeURIComponent(match[1]);
      }
    }
    return `${fallbackPrefix}_${new Date().toISOString().split('T')[0]}.csv`;
  }

  private downloadCsv(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private loadResults(): void {
    const form = this.filtersForm.value;
    if (form.problemType === null || form.problemType === undefined) {
      return;
    }

    this.spinnerService.show();
    this.familyStatusCheckService.search({
      problemType: form.problemType,
      countryId: form.countryId || undefined,
      year: form.year ? +form.year : undefined,
      capitalization: form.capitalization !== '' && form.capitalization !== null && form.capitalization !== undefined
        ? +form.capitalization
        : undefined,
      hideReviewed: form.hideReviewed ?? false,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (response) => {
        // Rows dropping out under "Hide reviewed" can leave the current page past the last one.
        const lastPage = Math.max(1, Math.ceil(response.totalCount / this.pageSize));
        if (this.currentPage > lastPage) {
          this.currentPage = lastPage;
          this.loadResults();
          return;
        }

        this.results = response.data;
        this.totalItems = response.totalCount;
        this.hasSearched = true;
        this.lastSearchedProblemType = form.problemType ?? null;
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error running family-status check');
      }
    });
  }
}
